import React, { useState, useRef, useEffect } from "react";
import { Modal, Input, Spin, Button } from "antd";
import * as R from "ramda";
import { useTranslation } from "react-i18next";
import Axios from "axios";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";

import styled from "styled-components/macro";

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  height: 500px;
`;

const Sidebar = styled.div`
  width: 300px;
  margin-right: 20px;
  display: flex;
  flex-direction: column;
`;

const LocationButton = styled.button`
  background: none;
  border: none;
  text-align: left;
  padding: 10px 15px;
  width: 100%;
  cursor: pointer;
  &.active {
    padding-left: 15px;
    border-right: 3px solid #3d64ff;
    background: #3d64ff42;
  }
  &:hover:not(.active) {
    background: #3d64ff42;
  }
  &:focus {
    outline: 0;
  }
  &:not(:last-child) {
    border-bottom: 1px solid #d3d3d363;
  }
`;

const ResultsContainer = styled.div`
  overflow: auto;
  flex: 1;
  margin-right: -20px;
  margin-top: 10px;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  .leaflet-preview-map {
    width: 100%;
    height: 100%;
    min-height: 500px;
    background: #f0f2f5;
  }
  p.search-disclaimer {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    text-align: center;
    width: 100%;
  }
`;

const SuggestDraw = styled.div`
  margin: 10px 0;
  > span {
    display: block;
    text-align: left;
    margin: 0 20px 10px 0;
    font-style: italic;
  }
`;

const statuses = {
  initial: "initial",
  loading: "loading",
  success: "success",
  error: "error"
};

const ENABLE_CUSTOM_AREA_DRAWING = true;

const mapStyle = {
  color: "#3d64ff",
  weight: 2,
  fillColor: "#3d64ff",
  fillOpacity: 0.25
};

const tileLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const tileLayerAttribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const getResultBounds = location => {
  const [south, north, west, east] = location.boundingbox || [];
  if (!south || !north || !west || !east) return null;

  return L.latLngBounds(
    [Number(south), Number(west)],
    [Number(north), Number(east)]
  );
};

const invalidateMapSize = map => {
  [0, 250, 500].forEach(delay => {
    window.setTimeout(() => {
      if (map) map.invalidateSize(false);
    }, delay);
  });
};

function SearchAreaModal({ onClose, onSubmit, onDraw }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const resultLayerRef = useRef(null);
  const selectedResultRef = useRef(null);
  const { t } = useTranslation(["global", "components"]);
  const [status, setStatus] = useState(statuses.initial);
  const [searchInput, setSearchInput] = useState("");
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    selectedResultRef.current = selectedResult;
  }, [selectedResult]);

  const renderSelectedResult = location => {
    if (!mapRef.current || !resultLayerRef.current) return;

    resultLayerRef.current.clearLayers();

    if (!location || !location.geojson) return;

    resultLayerRef.current.addData(location.geojson);

    const bounds =
      getResultBounds(location) || resultLayerRef.current.getBounds();
    if (bounds && bounds.isValid()) {
      mapRef.current.invalidateSize(false);
      mapRef.current.fitBounds(bounds.pad(0.2), { maxZoom: 12 });
    }
  };

  useEffect(() => {
    const initializeMap = () => {
      if (!mapContainerRef.current || mapRef.current) return;

      const map = L.map(mapContainerRef.current, {
        zoomControl: true,
        dragging: true,
        scrollWheelZoom: true,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        touchZoom: false,
        attributionControl: true
      }).setView([0, 0], 1);

      L.tileLayer(tileLayerUrl, {
        attribution: tileLayerAttribution,
        maxZoom: 19
      })
        .on("tileerror", error => {
          console.error("[Leaflet] tile load error", error);
        })
        .addTo(map);

      resultLayerRef.current = L.geoJSON(null, {
        style: mapStyle,
        pointToLayer: (_, latlng) =>
          L.circleMarker(latlng, {
            ...mapStyle,
            radius: 8
          })
      }).addTo(map);

      mapRef.current = map;
      invalidateMapSize(map);
      renderSelectedResult(selectedResultRef.current);
    };

    const timer = window.setTimeout(initializeMap, 300);

    return () => {
      window.clearTimeout(timer);
      if (mapRef.current) mapRef.current.remove();
      mapRef.current = null;
      resultLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    renderSelectedResult(selectedResult);
  }, [selectedResult]);

  const onSearch = async () => {
    try {
      setStatus(statuses.loading);
      const { data: searchResult } = await Axios(
        `https://nominatim.openstreetmap.org/search.php?q=${searchInput}&polygon_geojson=1&format=json&polygon_threshold=0.002&accept-language=es&limit=40`
      );
      if (!Array.isArray(searchResult) || searchResult.length === 0) {
        setResults([]);
        return setStatus(statuses.success);
      }
      const results =
        searchResult.filter(r =>
          ["Point", "Polygon", "MultiPolygon"].includes(
            R.path(["geojson", "type"], r)
          )
        ) || [];
      setResults(results);
      setStatus(statuses.success);
    } catch (e) {
      console.error(e);
      setResults([]);
      setStatus(statuses.error);
    }
  };

  const onSelectLocation = location => {
    setSelectedResult(location);
  };

  const submit = () => {
    if (!selectedResult) return;
    const {
      display_name,
      geojson,
      osm_id,
      osm_type,
      class: placeClass
    } = selectedResult;
    onSubmit({
      display_name,
      geojson,
      osm_id,
      osm_type: osm_type[0].toUpperCase(),
      class: placeClass
    });
    onClose();
  };

  return (
    <Modal
      width={800}
      title={t("components:SearchAreaModal.title")}
      okText={t("global:add")}
      cancelText={t("global:cancel")}
      onCancel={onClose}
      onOk={submit}
      okButtonProps={{
        disabled: !selectedResult
      }}
      visible={true}
    >
      <Layout>
        <Sidebar>
          <Input.Search
            enterButton
            placeholder={t("components:SearchAreaModal.searchPlaceholder")}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onSearch={onSearch}
          />
          {status === statuses.loading ? (
            <Spin style={{ margin: "20px 0" }} />
          ) : status === statuses.success || status === statuses.error ? (
            <ResultsContainer>
              {status === statuses.error ? (
                <p>{t("components:SearchAreaModal.searchError")}</p>
              ) : results.length === 0 ? (
                <p>{t("global:noResults")}</p>
              ) : (
                results.map(location => (
                  <LocationButton
                    key={location.place_id}
                    className={
                      location.place_id ===
                        (selectedResult && selectedResult.place_id) && "active"
                    }
                    onClick={() => onSelectLocation(location)}
                  >
                    {location.display_name}
                  </LocationButton>
                ))
              )}
              {ENABLE_CUSTOM_AREA_DRAWING && (
                <SuggestDraw>
                  <span>{t("components:SearchAreaModal.suggestDraw")}</span>
                  <Button onClick={onDraw}>
                    {t("components:SearchAreaModal.draw")}
                  </Button>
                </SuggestDraw>
              )}
            </ResultsContainer>
          ) : null}
        </Sidebar>
        <MapContainer>
          <div ref={mapContainerRef} className="leaflet-preview-map" />
          {!selectedResult && (
            <p className="search-disclaimer">
              {t("components:SearchAreaModal.overMapSuggestion")}
            </p>
          )}
        </MapContainer>
      </Layout>
    </Modal>
  );
}

export default SearchAreaModal;
